import { useEffect, useState, useRef } from "react";
import { Dialog } from "@headlessui/react";
import {
  XMarkIcon,
  PrinterIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import ResponseMessage from "../components/common/ResponseMessage";
import InputField from "../components/forms/InputField";
import useMultiFieldFormHandler from "../hooks/useMultiFieldFormHandler";
import api from "../api/axios";
import ActionDialog from "../components/common/ActionDialog";
import useAutoClear from "../hooks/useAutoClear";
import { downloadReport } from "../utils/downloadReport";

type Props = {
  open: boolean;
  onClose: () => void;
  onGenerate?: (params: any) => void; // optional callback
};

export default function SmvsPrint({ open, onClose, onGenerate }: Props) {
  const [branches, setBranches] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<
    { value: string; label: string }[]
  >([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [printSubmitting, setPrintSubmitting] = useState(false);
  const printModeRef = useRef<"normal" | "slip">("normal");

  // Auto-clear backend error messages after 4 seconds
  useAutoClear(backendError, () => setBackendError(null), 4000);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoadingBranches(true);
      setLoadingCurrencies(true);
      try {
        const [bRes, cRes] = await Promise.all([
          api.get("/master/select-branch/"),
          api.get("/shop/currencies/"),
        ]);
        setBranches(Array.isArray(bRes.data) ? bRes.data : []);
        if (Array.isArray(cRes.data)) {
          const opts = cRes.data.map((it: any) => ({
            value: String(it.code ?? it.id ?? it.display ?? ""),
            label: it.display ?? it.code ?? it.id ?? String(it),
          }));
          setCurrencies(opts);
        } else setCurrencies([]);
      } catch (err: any) {
        setBackendError(
          err?.response?.data?.message ||
            "Failed to load branches or currencies."
        );
      } finally {
        setLoadingBranches(false);
        setLoadingCurrencies(false);
      }
    };
    load();
  }, [open]);

  const form = useMultiFieldFormHandler(
    {
      date_from: "",
      date_to: "",
      branch_from: "",
      branch_to: "",
      currency: "",
      status: "",
    },
    {
      validate: (values) => {
        const errors: Partial<Record<string, string>> = {};
        // optional: require date_from/date_to
        if (!values.date_from.trim())
          errors.date_from = "Please select a start date.";
        if (!values.date_to.trim())
          errors.date_to = "Please select an end date.";
        // basic date order check
        if (values.date_from && values.date_to) {
          const a = Date.parse(values.date_from);
          const b = Date.parse(values.date_to);
          if (!isNaN(a) && !isNaN(b) && a > b) {
            errors.date_to = "End date must be the same or after start date.";
          }
        }
        // require currency and status
        if (!values.currency || !String(values.currency).trim()) {
          errors.currency = "Please select a currency.";
        }
        if (!values.status || !String(values.status).trim()) {
          errors.status = "Please select a status.";
        }

        // Branch selection: at least one branch must be selected (not both empty or 'select')
        const bf = String(values.branch_from ?? "").trim();
        const bt = String(values.branch_to ?? "").trim();
        const bfEmpty = bf === "" || bf === "select";
        const btEmpty = bt === "" || bt === "select";
        if (bfEmpty && btEmpty) {
          // single branch-level error (display once). Don't attach to individual fields
          errors.branch = "Please select at least one branch (From or To).";
        }
        return Object.keys(errors).length ? (errors as any) : null;
      },
      onSubmit: async (values) => {
        setBackendError(null);
        setPrintSubmitting(true);

        // Normalize branch params: if empty, send 'select'
        const params = {
          ...values,
          branch_from:
            values.branch_from && String(values.branch_from).trim()
              ? values.branch_from
              : "select",
          branch_to:
            values.branch_to && String(values.branch_to).trim()
              ? values.branch_to
              : "select",
        } as any;

        // If caller provided onGenerate, forward values and mode
        if (onGenerate) {
          try {
            onGenerate({ ...params, mode: printModeRef.current });
          } finally {
            setPrintSubmitting(false);
          }
          return;
        }

        const endpoint =
          printModeRef.current === "slip"
            ? `${api.defaults.baseURL}/shop/smvs-slip/`
            : `${api.defaults.baseURL}/shop/smvs-preview/`;

        // Default behaviour: request a report blob from backend and download
        try {
          await downloadReport(endpoint, params, "smvs_report", (msg) =>
            setBackendError(msg)
          );
        } catch (err: any) {
          setBackendError(
            err?.response?.data?.message || "Failed to generate report."
          );
        } finally {
          setPrintSubmitting(false);
        }
      },
      useDialog: true,
    }
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="fixed z-50 inset-0 flex items-center justify-center"
    >
      <div className="fixed inset-0 bg-black/70" aria-hidden />
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full z-10 p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        <Dialog.Title className="text-lg font-semibold mb-6 flex items-center gap-2">
          <ClipboardDocumentListIcon className="w-5 h-5 text-teal-600" /> SMVs
          Report
        </Dialog.Title>

        {backendError && (
          <div className="mb-3">
            <ResponseMessage variant="error" message={backendError} />
          </div>
        )}

        <form onSubmit={form.handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <InputField
              id="date_from"
              label="From"
              type="date"
              value={form.values.date_from}
              onChange={(e) => form.handleChange("date_from", e.target.value)}
              error={form.errors.date_from ?? undefined}
              icon={<CalendarDaysIcon className="w-4 h-4 text-teal-600" />}
            />

            <InputField
              id="date_to"
              label="To"
              type="date"
              value={form.values.date_to}
              onChange={(e) => form.handleChange("date_to", e.target.value)}
              error={form.errors.date_to ?? undefined}
              icon={<CalendarDaysIcon className="w-4 h-4 text-teal-600" />}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <InputField
              id="currency"
              label="Currency"
              as="select"
              value={form.values.currency}
              onChange={(e) => form.handleChange("currency", e.target.value)}
              options={currencies}
              disabled={loadingCurrencies}
              error={form.errors.currency ?? undefined}
              icon={<CurrencyDollarIcon className="w-4 h-4 text-teal-600" />}
            />

            <InputField
              id="status"
              label="Status"
              as="select"
              value={form.values.status}
              onChange={(e) => form.handleChange("status", e.target.value)}
              options={[
                { value: "", label: "Any" },
                { value: "I", label: "Initiated" },
                { value: "A", label: "Approved" },
                { value: "C", label: "Cancelled" },
              ]}
              error={form.errors.status ?? undefined}
              icon={<CheckCircleIcon className="w-4 h-4 text-teal-600" />}
            />
          </div>

          {(form.errors as any)?.branch && (
            <div className="mb-3">
              <ResponseMessage
                variant="warning"
                message={(form.errors as any).branch}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <InputField
              id="branch_from"
              label="Branch From"
              as="select"
              value={form.values.branch_from}
              onChange={(e) => {
                const val = e.target.value;
                form.handleChange("branch_from", val);
                // clear branch-level error if one branch is selected
                if (
                  val &&
                  String(val).trim() !== "" &&
                  String(val).trim() !== "select"
                ) {
                  form.setErrors((prev: any) => ({
                    ...prev,
                    branch: undefined,
                  }));
                }
              }}
              options={branches.map((b: any) => ({
                value: String(b.id ?? b.value ?? b),
                label: b.name ?? b.label ?? String(b),
              }))}
              disabled={loadingBranches}
              icon={<MapPinIcon className="w-4 h-4 text-teal-600" />}
            />

            <InputField
              id="branch_to"
              label="Branch To"
              as="select"
              value={form.values.branch_to}
              onChange={(e) => {
                const val = e.target.value;
                form.handleChange("branch_to", val);
                // clear branch-level error if one branch is selected
                if (
                  val &&
                  String(val).trim() !== "" &&
                  String(val).trim() !== "select"
                ) {
                  form.setErrors((prev: any) => ({
                    ...prev,
                    branch: undefined,
                  }));
                }
              }}
              options={branches.map((b: any) => ({
                value: String(b.id ?? b.value ?? b),
                label: b.name ?? b.label ?? String(b),
              }))}
              disabled={loadingBranches}
              icon={<MapPinIcon className="w-4 h-4 text-teal-600" />}
            />
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <button
              type="button"
              onClick={() => {
                printModeRef.current = "slip";
                form.handleSubmit({ preventDefault: () => {} } as any);
              }}
              disabled={printSubmitting}
              className={`bg-teal-600 text-white px-4 py-2 rounded inline-flex items-center ${
                printSubmitting
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-teal-700"
              }`}
            >
              {printSubmitting ? (
                "Please wait..."
              ) : (
                <>
                  <PrinterIcon className="w-4 h-4 mr-2" /> Slip
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                printModeRef.current = "normal";
                form.handleSubmit({ preventDefault: () => {} } as any);
              }}
              disabled={printSubmitting}
              className={`bg-teal-600 text-white px-4 py-2 rounded inline-flex items-center ${
                printSubmitting
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-teal-700"
              }`}
            >
              {printSubmitting ? (
                "Please wait..."
              ) : (
                <>
                  <PrinterIcon className="w-4 h-4 mr-2" /> Normal
                </>
              )}
            </button>
          </div>
        </form>

        {/* Confirmation dialog (uses form.dialogOpen set by the hook after validation) */}
        <ActionDialog
          isOpen={form.dialogOpen}
          title="Generate Report"
          message={
            printModeRef.current === "slip"
              ? "Generate SMVs slip now?"
              : "Generate SMVs report (preview) now?"
          }
          actionColor="#14b8a6"
          proceedLabel="Generate"
          cancelLabel="Cancel"
          onProceed={() => {
            form.handleProceed();
          }}
          onCancel={() => form.setDialogOpen(false)}
        />
      </div>
    </Dialog>
  );
}
